import type { TowerType, EnemyType } from '../core/types';

/**
 * Player-facing names. Internal type keys (patty, grunt, …) stay frozen for
 * the sim/balance/assets; these labels are the only thing shown in the HUD,
 * the selected-tower panel, and enemy tooltips.
 */
export const TOWER_NAMES: Record<TowerType, string> = {
  patty: 'Hambi Tower',
  pickle: 'Pickle Ranger',
  cheese: 'Cheese Guardian',
  bacon: 'Bacon Bombardier',
  sauce: 'Saucemaster',
  blade: 'The Cheese Bladesmen',
  bomb: 'BBQ Bomber',
  wise: 'Wise Hambi',
  wizard: 'Wizard Tower',
  sniper: 'General Sniper',
  darkhambi: 'Dark Hambi',
  ninja: 'Ninja Hambi',
  sensei: 'Sensei Ketchup',
  boxer: 'Boxer Hambi',
  gunner: 'Gunner Hambi',
  firemage: 'Fire Mage',
  aquasorcerer: 'Aqua Sorcerer',
  shrekbook: 'Shrekbook Hambi',
  phantom: 'Phantom Hambi',
  lightning: 'Lightning Hambi',
  spear: 'Spear Hambi',
  bow: 'Bow Hambi',
  capy: 'Capy',
  generalcapy: 'General Capy',
  galacticcapy: 'Galactic Capy',
  hog: 'Hog Hambi',
  magecapy: 'Mage Capy',
  minigunner: 'Mini Gunner',
  kingcapy: 'King Capy',
  kinghambi: 'King Hambi',
  dagger: 'Dagger Hambi',
  leafstorm: 'Leafstorm Hambi',
  samurai: 'Samurai Slicer',
  masher: 'Mayo Masher',
  grease: 'Grease Goddess',
  ultrahammy: 'Ultra Hammy',
  horror: 'Horror Hambi',
  demonic: 'Demonic Hambi',
  hamreaper: 'The Ham Reaper',
  healer: 'Healer Hambi',
  banker: 'Banking Burger',
  angelic: 'Angelic Hambi',
  goldenshroom: 'The Golden Shroom',
};

/**
 * One-line "what it does" blurbs shown in the first-start tutorial modal.
 * Only the Chapter 1 towers (patty–sauce) are ever surfaced, but all eight are
 * defined to satisfy the exhaustive Record type.
 */
export const TOWER_TUTORIALS: Record<TowerType, string> = {
  patty: 'Your reliable workhorse — steady single-target damage to the nearest nugget in range.',
  pickle: 'A long-range ranger whose shots pierce straight through a line of enemies.',
  cheese: 'Drips molten cheese to slow every enemy caught in its aura — great crowd control.',
  bacon: 'Lobs a splash of sizzling bacon bits, damaging tightly-packed clusters all at once.',
  sauce: 'Sauces enemies so every one of your towers deals bonus damage to them.',
  blade: 'A close-range brawler that slices any enemy that wanders next to it.',
  bomb: 'Fires a long-range bomb that detonates for heavy area damage on impact.',
  wise: 'Channels calm to freeze the entire map — pausing all enemies and spawns.',
  wizard:
    'Zaps nuggets with purple bolts every second — and every 15 seconds unleashes a mighty green blast.',
  sniper:
    'A long-range sharpshooter that picks off the nearest enemy with a single hard-hitting bullet.',
  darkhambi:
    'Lobs a dark poison that deals heavy damage over time — no upfront hit, just relentless rot.',
  ninja:
    'A stun-proof melee brawler that dashes in and lands a rapid three-hit sword flurry every strike.',
  sensei: 'A serene master who lobs a blob of ketchup at the nearest enemy every 0.6 seconds.',
  boxer:
    'A prizefighter who charges in, lands a heavy 100-damage punch, and is back on his feet in 0.3 seconds.',
  gunner:
    'A twin-pistol gunslinger who fires once a second — and one shot in three lands as a critical or a headshot for extra damage.',
  firemage:
    'Hurls a fireball every 1.2 seconds — and one shot in four burns blue for extra damage.',
  aquasorcerer:
    'Lobs a water ball that bursts on impact, soaking the enemy in front and the one behind for full damage too.',
  shrekbook:
    'Earned by felling 100 Shrekbruits in one Arena wave — fires the Hambi Tower’s shot once a second, for enormous damage.',
  phantom:
    'A spectral Hambi that shoots a ghost every 0.8 seconds — and at max tier charges up a red bullet for 1500 damage every 30 seconds.',
  lightning:
    'Map-wide: every 5 seconds a lightning cloud sets off from your base and rolls back up the track, striking up to 3 enemies for 500 damage each.',
  spear:
    'A long-reach charger who thrusts his spear into the nearest enemy within 250 px of his plot for 200 damage, then rests 0.4 seconds back home.',
  capy: 'A capybara who charges the nearest enemy within 100 px of his plot, bites for 100, and settles back on his plot for a full second before the next.',
  generalcapy:
    'Map-wide: every 3 seconds a capybara sets off from your base and runs back up the track, biting up to 3 enemies for 250 each — and one runner in six is a red imposter worth 750.',
  galacticcapy:
    'Throws a purple fireball every second for 300 — and every 5 seconds a black one for 500 that also catches the enemy ahead of and the one behind whatever it hits.',
  bow: 'An archer who looses an arrow every second for 350 — from tier 2 one arrow in four flies poisoned (400 a second for 3 seconds instead of a hit), and at tier 3 he shoots two enemies at once.',
  hog: 'A capybara with a burger on his back who charges the nearest enemy within 150 px of his plot, bites for 200, and settles back home for 1.2 seconds before the next.',
  magecapy:
    'A capybara wizard whose staff fires a red laser beam every 1.5 seconds for 250 — and at tier 3 the beam turns green and comes every single second.',
  minigunner:
    'A furious capybara behind a minigun, firing a bullet a second for 275 — one shot in five is a HEADSHOT worth 350, and 425 at tier 3.',
  kingcapy:
    'A crowned capybara who sends up to 3 red gems every 1.2 seconds, one each at 3 different enemies, for 400 apiece — at tier 3 each gem has a 1-in-5 chance of coming out blue for 700. When he is destroyed he fires 5 parting gems at random enemies anywhere on the map.',
  kinghambi:
    'The crowned Hambi throws a yellow shard every second for 400 — one in four splits in two and hits a second enemy. Every 30 seconds (20 at tier 3) the game stops for his K–O: a rain of shards falls on the whole map for 3000 on every enemy, and you earn a Golden Stone.',
  dagger:
    'A quick little Hambi with a dagger who dashes at the nearest enemy within 100 px, stabs for 200 and is ready again half a second later.',
  leafstorm:
    'Sends a spinning tornado of leaves every second that lands on its target and whirls into the 4 nearest enemies, hitting each one for 250.',
  samurai:
    'A Multi-attacker: he slashes the nearest enemy in his inner ring for 300 every second, and between slashes there is a 40% chance he throws 2 small daggers at 2 different enemies in his outer ring for 200 each.',
  masher:
    'Throws a sludge of mayo at the nearest enemy every second for 300 — and nothing can stun him.',
  grease:
    'Deals no damage herself: her grease slows every enemy in range by 50%, and every other tower in her range hits 25% harder.',
  ultrahammy:
    'Throws a blue fireball every second for 350, then dynamite, then purple crystals. At tier 4, every minute he yells END THEM: dynamite rains on every enemy for 5000, all towers hit 25% harder for 20 seconds and three mini Ultra Hammies join the fight. Only one at a time.',
  horror:
    'Throws dark red poison balls at 2 different enemies every 1.5 s. Each poisons for 350 a second for 2 s (400 at tier 2; tier 3 throws every second).',
  demonic:
    'Throws a flaming, spinning skull every 1.2 s that blows up in a dark purple blast, hitting every enemy nearby for 300 (400, then 495).',
  hamreaper:
    'A Multi-attacker: a dark flaming skull every second for 400 (500, then 650). Every 25 s (20 at tier 3) a huge scythe sweeps the screen right to left, cutting every enemy for 2000.',
  healer:
    'Never attacks. Every 10 s (7 s, then 5 s) he heals every tower in his range, himself too, by 2 HP. Towers only take damage in The Hub and the Origin Event.',
  banker:
    'Never attacks. Every enemy killed anywhere earns you 200 extra cash (300 at tier 2, 500 at tier 3). Only one at a time.',
  angelic:
    'Throws a yellow-white angelic fireball every second for 400 (550, then 650). At tier 3 each one has a 15% chance to be a CRITICAL for 700.',
  goldenshroom:
    'Throws a golden shroom every second for 250 (350, then 500). Every 25 s: SHROOM FRENZY — 900 to every enemy on the track.',
};

export const ENEMY_NAMES: Record<EnemyType, string> = {
  grunt: 'Nugget Trooper',
  dino: 'Dino Scout',
  fry: 'Fry Swarm',
  hotdog: 'Hotdog Brute',
  boss: 'The Golden Nugget',
  frost: 'Frozen Nugget',
  icedino: 'Frozen Dino',
  driller: 'The Dripstone Driller',
  nullnugget: 'The Null Nugget',
  ultranugget: 'The Ultra Nugget',
  jockey: 'The Nugget Jockey',
  watcher: 'The Crystal Watcher',
  nuggetguard: 'The Nugget Guard',
  kingnuggetron: 'King Nugget-tron',
  carrotcrusher: 'The Carrot Crusher',
  carrotninja: 'The Carrot Ninja',
  brocco: 'The Brocco',
  potatoslicer: 'The Potato Slicer',
  drycarrot: 'The Dry Carrot',
  minibell: 'The Mini Bell',
  emperorpepro: 'Emperor Pepro',
  caulifire: 'Caulifire',
  esparagusmage: 'The Esparagus Mage',
  ocraa: 'The Ocraa',
  bamboocrew: 'The Bamboo Crew',
  peproherald: 'Emperor Pepro',
  kingember: 'King Ember',
  shrekbruit: 'Shrekbruit',
  cosmiccorroder: 'Cosmic Corroder',
  nuisancenugget: 'Nuisance Nugget',
  barwaiter: 'Bar Waiter',
  burntfry: 'Burnt Fry',
  microwavednugget: 'Microwaved Nugget',
  combo199: '1.99 Combo',
  chiply: 'Chiply the Chip',
  microwave: 'The Microwave',
  chiplysdad: "Chiply's Dad",
  nacho: 'Nacho',
  seasonezchippington: 'Seasonez Chippington',
  lifeguard: 'The Lifeguard',
  moonnacho: 'Moon Nacho',
  spicednacho: 'Spiced Nacho',
  lasernacho: 'Laser Nacho',
  princenacho: 'Prince Nacho',
  princenacho2: 'Prince Nacho (2nd form)',
  ghost: 'Ghost',
  thedead: 'The Dead',
  darkghost: 'Dark Ghost',
  darknacho: 'Dark Nacho',
  jackoghost: "Jack'O Ghost",
  blueghost: 'Blue Ghost',
  reaper: 'The Shadow Reaper',
  winger: 'Winger',
  twilight: 'Twilight',
  nightfly: 'Nightfly',
  halolight: 'Halolight',
  halolight2: 'Halolight',
  halolight3: 'Halolight',
  lordturtleshell: 'Lord Turtleshell',
  redshroom: 'Redshroom',
  poisonshroom: 'Poison Shroom',
  skullshroom: 'Skullshroom',
  purplefungi: 'Purple Fungi',
  themushy: 'The Mushy',
  glowshroom: 'Glowshroom',
  blizzardfungi: 'Blizzard Fungi',
  zombieshroom: 'Zombieshroom',
  hynofungi: 'Hyno-Fungi',
  kingshroometron: 'King Shroometron',
};

/**
 * Weakened stand-ins that deliberately SHARE a stronger enemy's name. The Pepro
 * Herald IS Emperor Pepro, sent ahead of King Ember at reduced strength, so
 * ENEMY_NAMES gives both the same label — right for a run, where the player
 * should simply see the emperor coming.
 *
 * A list that offers BOTH at once cannot use that name twice: the Test Arena's
 * wave builder would show two identical "Emperor Pepro" rows with different
 * stats. Those surfaces append the note via `enemyNameWithVariant` instead.
 * Keep it out of the in-game hover label (GameScene) — naming the trick mid-run
 * spoils the encounter.
 */
export const ENEMY_VARIANT_NOTES: Partial<Record<EnemyType, string>> = {
  peproherald: 'Debuffed',
};

/** "Emperor Pepro (Debuffed)" — the display name plus any variant note. */
export function enemyNameWithVariant(type: EnemyType): string {
  const note = ENEMY_VARIANT_NOTES[type];
  return note ? `${ENEMY_NAMES[type]} (${note})` : ENEMY_NAMES[type];
}
